import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";

import { Context } from "../../../../store/teacher";
import { getGuidanceScreenPath } from "../../../../libs/ActiveLearning";
import { ReactComponent as EnageedLogo } from "../../../../assets/images/GlobalHeader/logo_enageed.svg";
import { ReactComponent as EnageedLogoSmall } from "../../../../assets/images/GlobalHeader/logo_enageed_small.svg";
import { ReactComponent as BellIcon } from "../../../../assets/images/GlobalHeader/icon_bell.svg";
import { ReactComponent as UserIcon } from "../../../../assets/images/GlobalHeader/icon_user.svg";
import { TEACHER_SESSION_KEY } from "../../../../Definition";
import useAppOrderStatus from "../../../../hooks/teacher/useAppOrderStatus";
import { submittedRealizationListPath } from "../../../../store/teacher";

import styles from "./GlobalHeader.module.scss";

type AppNavKey = "lounge" | "gear" | "core";
type ManagerNavKey = "introduction" | "manage" | "help";
type LinkType = "internal" | "app" | "external";
type NavItem = {
  labels: {
    full: string;
    short?: string;
  };
  link: string;
  linkType: LinkType;
};

const GlobalHeader = () => {
  const { contextState } = useContext(Context);
  const { school_name, is_manager, is_any_unread_news, teacher } = contextState;

  const guidanceScreenPath = getGuidanceScreenPath(
    contextState.available_item_codes,
  );

  const corePath = process.env.REACT_APP_TEACHER_DLP_URL
    ? `${process.env.REACT_APP_TEACHER_DLP_URL}${guidanceScreenPath}`
    : "";

  const APP_NAV_ITEMS: Record<AppNavKey, NavItem> = {
    lounge: {
      labels: {
        full: "LOUNGE -成長データベース-",
        short: "LOUNGE",
      },
      link: process.env.REACT_APP_TEACHER_ONE_MENU_URL ?? "",
      linkType: "app",
    },
    gear: {
      labels: {
        full: "GEAR -データ管理-",
        short: "GEAR",
      },
      link: submittedRealizationListPath,
      linkType: "internal",
    },
    core: {
      labels: {
        full: "CORE -教材-",
        short: "CORE",
      },
      link: corePath,
      linkType: "app",
    },
  };

  const MANAGER_NAV_ITEMS: Record<ManagerNavKey, NavItem> = {
    introduction: {
      labels: {
        full: "はじめに",
      },
      link: process.env.REACT_APP_TEACHER_ONE_MENU_URL
        ? `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/contents/outline`
        : "",
      linkType: "app",
    },
    manage: {
      labels: {
        full: "学校管理画面",
      },
      link: process.env.REACT_APP_MANAGER_URL ?? "",
      linkType: "app",
    },
    help: {
      labels: {
        full: "ヘルプ",
      },
      link: process.env.REACT_APP_TEACHER_HELP_URL ?? "",
      linkType: "external",
    },
  };

  const sessionId = sessionStorage.getItem(TEACHER_SESSION_KEY);

  const withSessionId = (url: string, type: LinkType): string => {
    if (!url) return url;
    if (type !== "app") return url;
    if (!sessionId) return url;

    const hasQuery = url.includes("?");
    const separator = hasQuery ? "&" : "?";

    return `${url}${separator}session_id=${sessionId}`;
  };

  const fullName = `${teacher.last_name} ${teacher.first_name}`;

  const {
    hasG4sAndDlpOrder,
    hasOnlyG4sOrder,
    hasDlpAndG4sBasicOrder,
    hasOnlyG4sBasicOrder,
  } = useAppOrderStatus();

  const appKeys = ((): AppNavKey[] => {
    if (hasG4sAndDlpOrder || hasOnlyG4sOrder) return ["lounge", "gear", "core"];
    if (hasDlpAndG4sBasicOrder || hasOnlyG4sBasicOrder) return ["core", "gear"];
    return ["core"];
  })();

  const manageKeys: ManagerNavKey[] = ["introduction", "manage", "help"];

  const homeNav = (() => {
    if (hasG4sAndDlpOrder || hasOnlyG4sOrder) return APP_NAV_ITEMS.lounge;

    return APP_NAV_ITEMS.core;
  })();

  return (
    <div className={styles.header}>
      <div className={styles.group}>
        {homeNav.linkType === "internal" ? (
          <NavLink className={styles.logo} to={homeNav.link}>
            <EnageedLogo className={styles.standard} />
            <EnageedLogoSmall className={styles.small} />
          </NavLink>
        ) : (
          <a
            className={styles.logo}
            href={withSessionId(homeNav.link, homeNav.linkType)}
          >
            <EnageedLogo className={styles.standard} />
            <EnageedLogoSmall className={styles.small} />
          </a>
        )}
        {appKeys.length > 0 && (
          <div className={styles.nav}>
            {appKeys.map(key => {
              const item = APP_NAV_ITEMS[key];

              const label = (
                <>
                  <span className={clsx(styles.label, styles.full)}>
                    {item.labels.full}
                  </span>
                  <span className={clsx(styles.label, styles.short)}>
                    {item.labels.short}
                  </span>
                </>
              );

              if (item.linkType === "app") {
                return (
                  <a
                    key={key}
                    className={styles.link}
                    href={withSessionId(item.link, item.linkType)}
                  >
                    {label}
                  </a>
                );
              }

              return (
                <NavLink
                  key={key}
                  className={clsx(styles.link, styles.isActive)}
                  to={item.link}
                >
                  {label}
                </NavLink>
              );
            })}
          </div>
        )}
        <div className={clsx(styles.nav, styles.manage)}>
          {manageKeys.map(key => {
            const item = MANAGER_NAV_ITEMS[key];
            const isExternal = item.linkType === "external";
            const isManage = key === "manage";

            if (!is_manager && key === "manage") return null;

            return (
              <a
                key={key}
                className={styles.link}
                href={withSessionId(item.link, item.linkType)}
                {...(isExternal || isManage
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {item.labels.full}
              </a>
            );
          })}
        </div>
        {/* エントリー受付時期以外は非表示「ENAGEED SUMMITは毎年恒例イベント」 */}
        {/* <div className={styles.nav}>
          <a
            className={clsx(styles.link, styles.summit)}
            href={`https://www.enageed.jp/summit/pre/2025`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ENAGEED SUMMIT 2025
            <br />- エントリー受付中 -
          </a>
        </div> */}
      </div>
      <div className={styles.group}>
        <a
          className={clsx(styles.link, styles.notify)}
          href={
            process.env.REACT_APP_TEACHER_ONE_MENU_URL
              ? withSessionId(
                  `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/news_list`,
                  "app",
                )
              : ""
          }
        >
          <BellIcon />
          {is_any_unread_news && <span className={styles.dot} />}
        </a>
        <a
          className={clsx(styles.link, styles.userInfo)}
          href={
            process.env.REACT_APP_TEACHER_ONE_MENU_URL
              ? withSessionId(
                  `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/profile_setting`,
                  "app",
                )
              : ""
          }
        >
          <UserIcon className={styles.userIcon} />
          <span className={styles.schoolName}>{school_name}</span>
          <span className={styles.userName}>{fullName}</span>
        </a>
      </div>
    </div>
  );
};

export default GlobalHeader;
